import {
  all,
  call,
  delay,
  fork,
  put,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import ApiConstants from 'src/common/helpers/ApiConstants';
import * as requests from 'src/common/helpers/HTTPRequest';
import { toastError, toastSuccess } from 'src/common/helpers/Notifications';
import TextConstants from 'src/common/helpers/TextConstants';
import {
  ALARM_EMAIL_ROUTING_ACTION,
  createAlarmEmailRoutingSettingCleanState,
  createAlarmEmailRoutingSettingError,
  createAlarmEmailRoutingSettingSuccess,
  deleteAlarmEmailRoutingSettingCleanState,
  deleteAlarmEmailRoutingSettingError,
  deleteAlarmEmailRoutingSettingSuccess,
  getAlarmEmailRoutingSettingsError,
  getAlarmEmailRoutingSettingsSuccess,
  sendTestNotificationError,
  sendTestNotificationSuccess,
  updateAlarmEmailRoutingHolidaysCleanState,
  updateAlarmEmailRoutingHolidaysError,
  updateAlarmEmailRoutingHolidaysSuccess,
  updateAlarmEmailRoutingModeError,
  updateAlarmEmailRoutingModeSuccess,
  updateAlarmEmailRoutingSettingCleanState,
  updateAlarmEmailRoutingSettingError,
  updateAlarmEmailRoutingSettingSuccess,
} from './actions';

function* getAlarmEmailRoutingSettingsSaga() {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.ALARM_EMAIL_ROUTING}`;
    const apiRes = yield call(requests.getRequest, url, {});

    // GET returns one combined payload: { settings, holidays, mode } (§5)
    const data = apiRes?.data || apiRes || {};

    yield put(
      getAlarmEmailRoutingSettingsSuccess({
        settings: data.settings || [],
        holidays: data.holidays || [],
        mode: data.mode || 'LIVE',  // #5614: default LIVE
      })
    );
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(getAlarmEmailRoutingSettingsError(e));
  }
}

function* createAlarmEmailRoutingSettingSaga({ payload }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.ALARM_EMAIL_ROUTING}`;
    const apiRes = yield call(requests.postRequest, url, payload, true);

    // The created item (incl. server-generated settingId) comes back in the
    // response; fall back to the raw body shape if it is not wrapped in data.
    const created = apiRes?.data || apiRes;

    yield put(createAlarmEmailRoutingSettingSuccess(created));

    toastSuccess(
      TextConstants.Common.AddedSuccessMessage.replace(
        '{item}',
        TextConstants.AlarmEmailRouting.Setting
      )
    );
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(createAlarmEmailRoutingSettingError(e));
  } finally {
    yield delay(500);
    yield put(createAlarmEmailRoutingSettingCleanState());
  }
}

function* updateAlarmEmailRoutingSettingSaga({ payload }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.ALARM_EMAIL_ROUTING}`;
    yield call(requests.putRequest, url, payload, true);

    // Echo the confirmed full-replace payload so the reducer can update the
    // row in place (health-monitor pattern).
    yield put(updateAlarmEmailRoutingSettingSuccess(payload));

    toastSuccess(
      TextConstants.Common.EditedSuccessMessage.replace(
        '{item}',
        TextConstants.AlarmEmailRouting.Setting
      )
    );
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(updateAlarmEmailRoutingSettingError(e));
  } finally {
    yield delay(500);
    yield put(updateAlarmEmailRoutingSettingCleanState());
  }
}

function* deleteAlarmEmailRoutingSettingSaga({ payload }) {
  try {
    const url = `${ApiConstants.BASE_URL}${
      ApiConstants.ALARM_EMAIL_ROUTING
    }/${encodeURIComponent(payload)}`;
    yield call(requests.deleteRequest, url, {}, true);

    yield put(deleteAlarmEmailRoutingSettingSuccess(payload));

    toastSuccess(
      TextConstants.Common.DeletedSuccessMessage.replace(
        '{item}',
        TextConstants.AlarmEmailRouting.Setting
      )
    );
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(deleteAlarmEmailRoutingSettingError(e));
  } finally {
    yield delay(500);
    yield put(deleteAlarmEmailRoutingSettingCleanState());
  }
}

function* updateAlarmEmailRoutingHolidaysSaga({ payload }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.ALARM_EMAIL_ROUTING_HOLIDAYS}`;
    // Replace-all semantics: the payload is the full dates list (§5)
    yield call(requests.putRequest, url, { dates: payload || [] }, true);

    yield put(updateAlarmEmailRoutingHolidaysSuccess(payload || []));

    toastSuccess(
      TextConstants.Common.EditedSuccessMessage.replace(
        '{item}',
        TextConstants.AlarmEmailRouting.UserHolidays
      )
    );
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(updateAlarmEmailRoutingHolidaysError(e));
  } finally {
    yield delay(500);
    yield put(updateAlarmEmailRoutingHolidaysCleanState());
  }
}

function* sendTestNotificationSaga({ payload }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.NOTIFICATIONS}`;
    // payload is already the request body: { destinations: [...] }
    const apiRes = yield call(requests.postRequest, url, payload, true);

    yield put(sendTestNotificationSuccess());

    // The backend owns the success wording (§A2-2) — surface it verbatim.
    toastSuccess(apiRes?.message);
  } catch (e) {
    console.log(e);
    // A body-less 403 (role not permitted) normalizes to message '' — fall
    // back so the toast is never empty.
    toastError(e.message || TextConstants.AlarmEmailRouting.TestSendFailed);
    yield put(sendTestNotificationError(e));
  }
}

function* updateAlarmEmailRoutingModeSaga({ payload }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.ALARM_EMAIL_ROUTING_MODE}`;
    yield call(requests.putRequest, url, { mode: payload }, true);
    yield put(updateAlarmEmailRoutingModeSuccess(payload));
    toastSuccess(
      TextConstants.Common.EditedSuccessMessage.replace(
        '{item}',
        TextConstants.AlarmEmailRouting.Mode
      )
    );
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(updateAlarmEmailRoutingModeError(e));
  }
}

export function* AlarmEmailRoutingSaga() {
  yield takeLatest(
    ALARM_EMAIL_ROUTING_ACTION.GET_ALARM_EMAIL_ROUTING_SETTINGS,
    getAlarmEmailRoutingSettingsSaga
  );
  yield takeLatest(
    ALARM_EMAIL_ROUTING_ACTION.CREATE_ALARM_EMAIL_ROUTING_SETTING,
    createAlarmEmailRoutingSettingSaga
  );
  // takeEvery, NOT takeLatest: the grid's enabled toggle can fire on several
  // independent rows in quick succession — takeLatest would cancel an
  // in-flight PUT/DELETE whose server write still lands, leaving the grid
  // silently stale (health-monitor precedent).
  yield takeEvery(
    ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_SETTING,
    updateAlarmEmailRoutingSettingSaga
  );
  yield takeEvery(
    ALARM_EMAIL_ROUTING_ACTION.DELETE_ALARM_EMAIL_ROUTING_SETTING,
    deleteAlarmEmailRoutingSettingSaga
  );
  yield takeLatest(
    ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_HOLIDAYS,
    updateAlarmEmailRoutingHolidaysSaga
  );
  // takeLatest: rapid re-clicks should only honor the latest send (the button
  // is also disabled while sendingTest is true).
  yield takeLatest(
    ALARM_EMAIL_ROUTING_ACTION.SEND_TEST_NOTIFICATION,
    sendTestNotificationSaga
  );
  yield takeLatest(
    ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_MODE,
    updateAlarmEmailRoutingModeSaga
  );
}

export default function* rootSaga() {
  yield all([fork(AlarmEmailRoutingSaga)]);
}
